import React from 'react';
import { CheckCircle2Icon, AlertTriangleIcon, XCircleIcon } from 'lucide-react';
import { AVAILABILITY_CLASS_META, formatDowntime, type LinkBudgetResult as LinkBudgetData } from '../utils/linkBudget';
interface LinkBudgetResultProps {
  budget: LinkBudgetData;
}
export function LinkBudgetResult({
  budget
}: LinkBudgetResultProps) {
  const meta = AVAILABILITY_CLASS_META[budget.availabilityClass];
  const downtime = formatDowntime(budget.outageMinutesPerYear);
  const VerdictIcon = budget.verdict === 'PASS' ? CheckCircle2Icon : budget.verdict === 'MARGINAL' ? AlertTriangleIcon : XCircleIcon;
  return <div className="mt-4 space-y-3" data-id="element-220" data-mp="1wv0oo3hegsct">
      <div className="flex flex-col xl:flex-row gap-3" data-id="element-221" data-mp="1rucmqmrmur3t">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1" data-id="element-222" data-mp="1rtjknz3mcps0">
          <BigStat label="RSL Terima" value={`${budget.rsl.toFixed(1)} dBm`} data-id="element-223" data-mp="leiqnyy5mho2" />
          <BigStat label="RX Threshold" value={`${budget.rxThreshold.toFixed(1)} dBm`} data-id="element-224" data-mp="gd36e93swf6q" />
          <BigStat label="Fade Margin" value={`${budget.fadeMargin.toFixed(1)} dB`} valueColor={budget.fadeMargin >= 20 ? '#34d399' : meta.accent} data-id="element-225" data-mp="xus250ie1xyj" />

          {/* Availability — tile yang ditekankan */}
          <div className="px-4 py-3 rounded-xl bg-cyan-500/[0.07] border border-cyan-400/35 shadow-glow" data-id="element-226" data-mp="ttydu611ah54">
            <div className="text-[9px] text-cyan-300/80 uppercase tracking-[0.14em] font-semibold" data-id="element-227" data-mp="f5266a2w3651">
              Availability
            </div>
            <div className="text-xl font-mono font-bold text-cyan-200 mt-1 leading-none" data-id="element-228" data-mp="jyodrnu74jtz">
              {budget.availability.toFixed(3)} %
            </div>
            <div className="text-[10px] text-slate-500 mt-1.5 font-mono" data-id="element-229" data-mp="a7toealnl44b">
              ≈ {downtime}
            </div>
            <span className={`inline-block mt-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full border uppercase tracking-wider ${meta.tone}`} data-id="element-230" data-mp="3sdf1tqbk3kt0">
              {meta.chip}
            </span>
          </div>
        </div>

        {/* Verdict */}
        <div className={`flex xl:flex-col items-center xl:items-start justify-center gap-3 xl:gap-2 px-5 py-4 rounded-xl border xl:w-72 ${meta.verdictTone}`} data-id="element-231" data-mp="1y3qdb9bfi0q9">
          <div className="flex items-center gap-2.5" data-id="element-232" data-mp="1p4mtg34fthap">
            <VerdictIcon size={26} className="flex-shrink-0" data-id="element-233" data-mp="1p20branu1ler" />
            <span className="text-2xl font-bold tracking-[0.1em]" data-id="element-234" data-mp="2ltcdo03jelzu">
              {budget.verdict}
            </span>
          </div>
          <p className="text-[11px] text-slate-400 leading-relaxed" data-id="element-235" data-mp="2htk3jiyhw7sd">
            Fade margin {budget.fadeMargin.toFixed(1)} dB menghasilkan
            availability {budget.availability.toFixed(3)}% (setara {downtime}{' '}
            downtime).
          </p>
        </div>
      </div>

      <Waterfall budget={budget} data-id="element-236" data-mp="11g3krmhh72i3" />
    </div>;
}
function BigStat({
  label,
  value,
  valueColor
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  return <div className="px-4 py-3 rounded-xl bg-slate-900/70 border border-slate-700/70" data-id="element-237" data-mp="mck1te7ktp8y">
      <div className="text-[9px] text-slate-500 uppercase tracking-[0.14em] font-semibold" data-id="element-238" data-mp="r9sl5i4gnzbs">
        {label}
      </div>
      <div className="text-xl font-mono font-bold mt-1 leading-none" style={{
      color: valueColor || '#e2e8f0'
    }} data-id="element-239" data-mp="w8wss4bqlp00">
        {value}
      </div>
    </div>;
}
function Waterfall({
  budget
}: {
  budget: LinkBudgetData;
}) {
  const total = budget.waterfall.reduce((sum, segment) => sum + Math.abs(segment.value), 0);
  return <div data-id="element-240" data-mp="1f0fv08xxsz0z">
      <div className="flex items-center justify-between mb-1.5" data-id="element-241" data-mp="1ezmxlykvblvi">
        <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-[0.14em]" data-id="element-242" data-mp="1ey3jadirvkwd">
          Link Budget
        </span>
        <span className="text-[10px] font-mono text-slate-400" data-id="element-243" data-mp="3gs2p4jd4la1o">
          RSL {budget.rsl.toFixed(1)} dBm
        </span>
      </div>

      <div className="flex h-8 rounded-lg overflow-hidden border border-slate-800" data-id="element-244" data-mp="3lrfcd57wsj4s">
        {budget.waterfall.map(segment => {
        const width = total > 0 ? Math.abs(segment.value) / total * 100 : 0;
        if (width <= 0) return null;
        return <div key={segment.label} className="flex items-center justify-center min-w-0 border-r border-slate-950/60 last:border-r-0" style={{
          width: `${width}%`,
          background: `${segment.color}26`
        }} title={`${segment.label}: ${segment.value > 0 ? '+' : ''}${segment.value.toFixed(1)} dB`} data-id="element-245" data-mp="1p3js3tcryga7">
              <span className="text-[9px] font-mono font-bold truncate px-1" style={{
            color: segment.color
          }} data-id="element-246" data-mp="164fm6tgj52mr">
                {segment.value > 0 ? '+' : ''}
                {segment.value.toFixed(1)}
              </span>
            </div>;
      })}
      </div>

      <div className="flex mt-1" data-id="element-247" data-mp="8o0hr8eyf9t3">
        {budget.waterfall.map(segment => {
        const width = total > 0 ? Math.abs(segment.value) / total * 100 : 0;
        if (width <= 0) return null;
        return <div key={segment.label} className="text-center min-w-0" style={{
          width: `${width}%`
        }} data-id="element-248" data-mp="2p458o9d4b5vi">
              <span className="text-[8px] text-slate-500 uppercase tracking-wider truncate block px-0.5" data-id="element-249" data-mp="2g9quyb88k3ei">
                {segment.label}
              </span>
            </div>;
      })}
      </div>
    </div>;
}